'use client';

import { ArrowForward, Check } from '@mui/icons-material';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { ChangeEvent, FocusEvent, ReactNode, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { trackEvent } from '@/lib/analytics/gtm';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { getPageCodeFromPath } from '@/lib/helpers/pageHelpers';
import { buildUrlWithUtmParams } from '@/lib/helpers/utmParamHelpers';
import { ComponentBlocksFunnel } from '@/types';

const buildUrlWithAmount = (url?: string | null, amount?: string): string => {
  if (!url) return '#';
  if (!url.includes('apply.lend')) return url;
  if (!amount) return url;

  const encodedAmount = encodeURIComponent(amount);
  return url.includes('?')
    ? `${url}${url.endsWith('?') || url.endsWith('&') ? '' : '&'}amount=${encodedAmount}`
    : `${url}?amount=${encodedAmount}`;
};

type FunnelLeadCaptureSidebarProps = {
  block: ComponentBlocksFunnel;
  children?: ReactNode;
  fixedSidebar?: boolean;
};

export function FunnelLeadCaptureSidebar({
  block,
  children,
  fixedSidebar = false,
}: FunnelLeadCaptureSidebarProps) {
  const theme = useTheme();
  const pathname = usePathname();
  const {
    FunnelLogo,
    FunnelHeader,
    FunnelSubHeader,
    FunnelContents,
    FunnelFootNote,
    FunnelButtonLabel,
    InputLabel,
    InputPlaceholder,
    InputLink,
  } = block;

  const [amount, setAmount] = useState('50000');
  const [displayValue, setDisplayValue] = useState('50,000.00');
  const showAmountInput = Boolean(InputLabel || InputPlaceholder);

  const formatCurrency = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^0-9.]/g, '');
    const parts = input.split('.');
    if (parts.length > 2 || parts[1]?.length > 2) return;
    setAmount(input);
    setDisplayValue(input); // show exactly what user types
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    const formatted = formatCurrency(amount);
    setDisplayValue(formatted);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    // remove commas for easier editing
    setDisplayValue(amount);
    e.target.select();
  };

  const baseHref =
    InputLink && (!showAmountInput || amount !== '')
      ? buildUrlWithAmount(InputLink, showAmountInput ? amount : undefined)
      : undefined;
  // Decorate the apply link with UTM params derived from the current page path
  // so leads trace back to the exact page (matches commercial-page CTA behaviour).
  const validHref = baseHref
    ? buildUrlWithUtmParams(baseHref, getPageCodeFromPath(pathname))
    : undefined;

  return (
    <Box
      component="aside"
      sx={{
        background: theme.palette.purple[100],
        borderRadius: 3,
        border: `1px solid ${theme.palette.purple[200]}`,
        p: fixedSidebar ? { xs: 2, md: 2.5 } : { xs: 3, md: 3.5 },
        textAlign: 'center',
        color: theme.palette.purple[900],
      }}
    >
      {/* Logo */}
      {getImageUrl({ image: FunnelLogo, size: 'url' }) && (
        <Box mb={{ xs: 1.5, md: 2 }}>
          <Image
            src={getImageUrl({ image: FunnelLogo, size: 'url' })!}
            alt={FunnelLogo?.alternativeText || 'Logo'}
            width={120}
            height={32}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Box>
      )}

      {/* Header */}
      {FunnelHeader && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: { xs: 1, md: 2 },
            fontSize: { xs: 16, md: 18 },
            color: theme.palette.brand.primarySecondary1,
          }}
        >
          {FunnelHeader}
        </Typography>
      )}

      {/* SubHeader */}
      {FunnelSubHeader && (
        <Typography
          variant="body2"
          sx={{
            mb: { xs: 2, md: 2.5 },
            color: theme.palette.purple[800],
            fontSize: { xs: 14, md: 15 },
            lineHeight: 1.55,
          }}
        >
          {FunnelSubHeader}
        </Typography>
      )}

      {FunnelContents && (
        <Box
          sx={{
            mb: { xs: 2, md: 2.5 },
            textAlign: 'left',
            color: theme.palette.purple[900],
            '& ul': {
              listStyle: 'none',
              p: 0,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            },
            '& li': {
              display: 'grid',
              gridTemplateColumns: '1.25rem 1fr',
              gap: 1.25,
              alignItems: 'start',
              fontSize: 14,
              lineHeight: 1.45,
              '&::before': {
                content: '""',
                display: 'none',
              },
            },
            '& li > *': {
              m: 0,
            },
            '& p': {
              m: 0,
              fontSize: 14,
              lineHeight: 1.45,
            },
          }}
        >
          <GlobalBlockRenderer
            content={FunnelContents}
            overrides={{
              blocks: {
                'list-item': ({ children }: { children: ReactNode }) => (
                  <Box component="li">
                    <Check
                      sx={{
                        fontSize: 17,
                        color: theme.palette.brand.primarySecondary1,
                        mt: '0.1rem',
                      }}
                    />
                    <Box>{children}</Box>
                  </Box>
                ),
              },
              modifiers: {},
            }}
          />
        </Box>
      )}

      {/* Input Label */}
      {showAmountInput && InputLabel && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            mb: { xs: 1, md: 1.5 },
            display: 'block',
            fontSize: { xs: 14, md: 15 },
          }}
        >
          {InputLabel}
        </Typography>
      )}

      {/* Input Field */}
      {showAmountInput && (
        <TextField
          fullWidth
          placeholder={InputPlaceholder || ''}
          variant="outlined"
          size="small"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          InputProps={{
            sx: {
              height: 40,
              fontSize: { xs: 14, md: 15 },
              px: 1,
              bgcolor: theme.palette.common.white,
              borderRadius: 8,
            },
          }}
          sx={{ mb: 1 }}
        />
      )}

      {/* Button */}
      {InputLink && (
        <Box
          sx={{
            a: {
              textDecoration: 'none',
              borderRadius: 20,
              background: theme.palette.gradients.gradientCta,
              color: theme.palette.common.white,
              '&:hover': {
                background: theme.palette.gradients.gradient1,
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: { xs: 14, md: 15 },
              fontWeight: 600,
              minHeight: 48,
              mt: showAmountInput ? 1 : 0,
              width: '100%',
              border: `1px solid ${theme.palette.purple[500]}`,
              boxShadow: 'none !important',
              transition: 'background 0.2s ease-in-out, color 0.2s ease-in-out',
            },
            '&[data-disabled="true"] a': {
              cursor: 'not-allowed',
              background: theme.palette.purple[300],
              pointerEvents: 'none',
            },
          }}
          data-disabled={!validHref}
        >
          <a
            href={validHref}
            target="_blank"
            rel="nofollow noopener"
            aria-disabled={!validHref}
            tabIndex={validHref ? 0 : -1}
            onClick={() =>
              trackEvent({ feature: 'funnel', action: 'funnel_cta_click', isKeyEvent: true })
            }
          >
            {FunnelButtonLabel || 'Get started'}&nbsp;
            <ArrowForward fontSize="small" />
          </a>
        </Box>
      )}

      {FunnelFootNote && (
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: theme.palette.purple[900],
            fontWeight: 600,
          }}
        >
          {FunnelFootNote}
        </Typography>
      )}

      {children}
    </Box>
  );
}
